import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/testimonials';
import { useState } from 'react';

export default function About() {
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const phoneAlt = formData.get('phone_alt') as string;
    if (phoneAlt && phoneAlt.trim() !== '') {
      setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
      setSubmitting(false);
      return;
    }

    try {
      const resp = await fetch('https://readdy.ai/api/form/d9pn0gmb4qjhggaeq1f0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, v as string])
        ).toString(),
      });

      const respText = await resp.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(respText); } catch { /* ignore */ }

      if (resp.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
        form.reset();
      } else {
        const serverMsg = (parsed as Record<string, unknown>)?.meta &&
          (typeof (parsed as Record<string, unknown>).meta === 'object')
          ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message
          : undefined;
        setFormStatus({ type: 'error', message: serverMsg || '提交失败，请稍后再试。' });
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请检查网络后重试。' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Schema.org */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "关于我们 - 爱情岛独家",
        "description": "爱情岛独家是领先的高品质内容平台，致力于为用户提供最权威、最私密的独家内容体验。了解我们的品牌故事、核心优势和用户口碑。",
        "url": "https://aiqingdao.com/about",
        "isPartOf": {
          "@type": "WebSite",
          "name": "爱情岛独家",
          "url": "https://aiqingdao.com"
        }
      }) }} />

      {/* Breadcrumb */}
      <div className="pt-20 bg-background-50 border-b border-background-200/60">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">关于我们</span>
          </nav>
        </div>
      </div>

      {/* Brand Intro */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14">
            <div className="flex-1">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-5">
                关于爱情岛独家
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-4">
                爱情岛独家成立于2020年，是国内领先的高品质内容平台。我们专注于为用户提供最权威、最私密的独家内容体验，汇聚海量精选资源，带给您与众不同的专属享受。
              </p>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                平台安全稳定，内容持续更新，涵盖独家精品、高清资源、最新推荐、经典回顾、深度解读、热门排行等六大核心品类。每一篇内容都经过专业编辑团队的严格筛选，确保原创性、品质感和价值性。
              </p>
              <div className="flex flex-wrap gap-3">
                {['品质优先', '安全稳定', '持续更新', '用户至上'].map((v) => (
                  <span key={v} className="px-4 py-1.5 text-xs font-medium bg-primary-50 text-primary-700 rounded-full">
                    {v}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-1 w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20modern%20office%20space%20with%20warm%20ambient%20lighting%2C%20minimalist%20furniture%2C%20bookshelves%20with%20curated%20collections%2C%20cream%20and%20beige%20tones%2C%20peaceful%20creative%20atmosphere%2C%20editorial%20interior%20photography%2C%20high%20detail%2C%20professional%20yet%20cozy&width=700&height=500&seq=about-brand-01&orientation=landscape"
                alt="爱情岛独家品牌形象"
                className="w-full rounded-2xl object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-14 md:py-18 bg-background-100">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-5">
            {[
              {
                icon: 'ri-eye-line',
                title: '我们的愿景',
                desc: '成为国内最受信赖的高品质内容平台，让每一位用户都能在这里找到属于自己的精神家园。',
              },
              {
                icon: 'ri-flag-line',
                title: '我们的使命',
                desc: '为用户筛选并呈现最具价值的独家内容，以品质定义内容消费的行业标准。',
              },
              {
                icon: 'ri-heart-line',
                title: '核心价值观',
                desc: '品质为先、用户至上、持续创新、真实可信。这是我们不变的承诺和追求。',
              },
            ].map((item) => (
              <div key={item.title} className="text-center p-6 md:p-8 bg-background-50 rounded-2xl border border-background-200/60">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${item.icon} text-2xl text-primary-600`}></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 md:py-18 bg-primary-500">
        <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '2020', label: '创立年份' },
              { value: '12,000+', label: '精选内容' },
              { value: '80万+', label: '月活用户' },
              { value: '98%', label: '用户好评率' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 mb-1">{s.value}</div>
                <div className="text-sm text-background-50/70">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              用户口碑
            </h2>
            <p className="text-foreground-600 text-sm md:text-base">
              来自真实用户的声音，是我们前行的最大动力
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.slice(0, 3).map((t) => (
              <div key={t.id} className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/60">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-14 md:py-20 bg-background-100">
        <div className="w-full max-w-2xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm md:text-base">
              有任何问题或建议？请随时与我们取得联系
            </p>
          </div>

          <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/60">
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-800"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-800"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-800"
                  placeholder="请输入咨询主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-800 resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                ></textarea>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="phone_alt"
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 font-medium rounded-full transition-colors whitespace-nowrap text-base cursor-pointer"
              >
                {submitting ? '提交中...' : '提交留言'}
              </button>

              {formStatus && (
                <div className={`text-sm text-center p-3 rounded-lg ${
                  formStatus.type === 'success'
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  {formStatus.message}
                </div>
              )}
            </form>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            {[
              { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@aiqingdao.com' },
              { icon: 'ri-phone-line', title: '客服电话', value: '400-888-8888' },
              { icon: 'ri-map-pin-line', title: '办公地址', value: '北京市朝阳区' },
            ].map((item) => (
              <div key={item.title} className="text-center p-4 bg-background-50 rounded-xl border border-background-200/60">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-primary-50">
                  <i className={`${item.icon} text-lg text-primary-600`}></i>
                </div>
                <div className="text-xs text-foreground-500 mb-0.5">{item.title}</div>
                <div className="text-sm font-medium text-foreground-800">{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}